import React, { useState, useEffect, useCallback } from 'react';
import { cmsAdmin } from '../services/cmsAPI';
import toast from 'react-hot-toast';

// ── Minimal rich-text editor using a plain textarea with helper buttons ──
function RichEditor({ value, onChange }) {
  const wrap = (tag) => {
    const sel = window.getSelection()?.toString();
    if (sel) onChange(value.replace(sel, `<${tag}>${sel}</${tag}>`));
    else onChange(value + `<${tag}></${tag}>`);
  };
  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden">
      <div className="flex gap-1 p-2 bg-gray-800 border-b border-gray-700 flex-wrap">
        {[['B','b'],['I','i'],['U','u']].map(([lbl,tag]) => (
          <button key={tag} type="button" onClick={() => wrap(tag)}
            className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded text-white font-bold">{lbl}</button>
        ))}
        {[['H2','h2'],['H3','h3'],['P','p'],['UL','ul'],['LI','li']].map(([lbl,tag]) => (
          <button key={tag} type="button" onClick={() => wrap(tag)}
            className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded text-white">{lbl}</button>
        ))}
        <button type="button" onClick={() => onChange(value + '<a href=""></a>')}
          className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded text-white">Link</button>
        <span className="text-xs text-gray-400 ml-2 self-center">HTML editor</span>
      </div>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={14}
        className="w-full bg-gray-900 text-gray-100 text-sm p-3 font-mono focus:outline-none resize-y"
        placeholder="Enter HTML content..."
      />
    </div>
  );
}

// ── Status badge ─────────────────────────────────────────────
function StatusBadge({ status }) {
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${status === 'published' ? 'bg-green-900 text-green-300' : 'bg-yellow-900 text-yellow-300'}`}>
      {status === 'published' ? 'Published' : 'Draft'}
    </span>
  );
}

// ────────────────────────────────────────────────────────────
// TAB: Company Profile
// ────────────────────────────────────────────────────────────
function CompanyTab() {
  const [form, setForm] = useState({ name:'', tagline:'', regNumber:'', tin:'', merchantNumber:'', logo:'', favicon:'', md:'', mdPhone:'', mdEmail:'', website:'', copyright:'', status:'published' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cmsAdmin.getCompany().then(r => { if (r.data && r.data._id) setForm(f => ({ ...f, ...r.data })); }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    try { await cmsAdmin.updateCompany(form); toast.success('Saved as draft'); }
    catch (e) { toast.error(e.message || 'Save failed'); }
  };

  const publish = async () => {
    try {
      await cmsAdmin.updateCompany(form);
      await cmsAdmin.publishCompany();
      setForm(f => ({ ...f, status: 'published' }));
      toast.success('Published successfully');
    } catch (e) { toast.error(e.message || 'Publish failed'); }
  };

  const rollback = async () => {
    if (!window.confirm('Roll back to previous version?')) return;
    try {
      const r = await cmsAdmin.rollbackCompany();
      if (r.data) setForm(f => ({ ...f, ...r.data }));
      toast.success('Rolled back');
    } catch (e) { toast.error(e.message || 'Rollback failed'); }
  };

  const field = (label, key, type = 'text') => (
    <div key={key}>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <input type={type} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500" />
    </div>
  );

  if (loading) return <div className="text-gray-400 p-4">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Company Profile</h2>
        <div className="flex gap-2">
          <StatusBadge status={form.status} />
          <button onClick={rollback} className="px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 rounded text-white">Rollback</button>
          <button onClick={save} className="px-3 py-1.5 text-xs bg-blue-700 hover:bg-blue-600 rounded text-white">Save Draft</button>
          <button onClick={publish} className="px-3 py-1.5 text-xs bg-green-700 hover:bg-green-600 rounded text-white font-semibold">Publish</button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {field('Company Name', 'name')}
        {field('Tagline', 'tagline')}
        {field('Registration Number', 'regNumber')}
        {field('TIN', 'tin')}
        {field('Merchant Number', 'merchantNumber')}
        {field('Website', 'website', 'url')}
        {field('Logo URL', 'logo', 'url')}
        {field('Favicon URL', 'favicon', 'url')}
        {field('Managing Director', 'md')}
        {field('MD Phone', 'mdPhone', 'tel')}
        {field('MD Email', 'mdEmail', 'email')}
      </div>
      <div>
        <label className="block text-xs text-gray-400 mb-1">Copyright Text</label>
        <input type="text" value={form.copyright || ''} onChange={e => setForm(f => ({ ...f, copyright: e.target.value }))}
          className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500" />
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// TAB: Country Profiles
// ────────────────────────────────────────────────────────────
function CountriesTab() {
  const [countries, setCountries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ countryCode:'', countryName:'', officeAddress:'', phones:[''], emails:[''], mapLink:'', whatsapp:'', businessHours:{ weekdays:'Mon-Fri: 8:00 AM - 6:00 PM', saturday:'Sat: 9:00 AM - 4:00 PM', sunday:'Closed' } });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    cmsAdmin.getCountries().then(r => setCountries(r.data || [])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const select = (c) => {
    setSelected(c);
    setForm({ ...c, phones: c.phones?.length ? c.phones : [''], emails: c.emails?.length ? c.emails : [''], businessHours: c.businessHours || { weekdays:'Mon-Fri: 8:00 AM - 6:00 PM', saturday:'Sat: 9:00 AM - 4:00 PM', sunday:'Closed' } });
  };

  const newCountry = () => {
    setSelected(null);
    setForm({ countryCode:'', countryName:'', officeAddress:'', phones:[''], emails:[''], mapLink:'', whatsapp:'', businessHours:{ weekdays:'Mon-Fri: 8:00 AM - 6:00 PM', saturday:'Sat: 9:00 AM - 4:00 PM', sunday:'Closed' } });
  };

  const save = async () => {
    try {
      if (selected) { await cmsAdmin.updateCountry(selected.countryCode, form); }
      else { await cmsAdmin.saveCountry(form); }
      toast.success('Saved as draft'); load();
    } catch (e) { toast.error(e.message || 'Save failed'); }
  };

  const publish = async () => {
    const code = selected?.countryCode || form.countryCode;
    if (!code) return toast.error('Save first');
    try {
      await save();
      await cmsAdmin.publishCountry(code);
      toast.success('Published'); load();
    } catch (e) { toast.error(e.message || 'Publish failed'); }
  };

  const del = async (code) => {
    if (!window.confirm('Delete country profile for ' + code + '?')) return;
    try { await cmsAdmin.deleteCountry(code); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  const arrField = (key, label) => (
    <div key={key}>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      {(form[key] || ['']).map((v, i) => (
        <div key={i} className="flex gap-2 mb-1">
          <input type="text" value={v} onChange={e => { const a = [...(form[key] || [''])]; a[i] = e.target.value; setForm(f => ({ ...f, [key]: a })); }}
            className="flex-1 bg-gray-800 border border-gray-600 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-cyan-500" />
          <button type="button" onClick={() => { const a = (form[key] || ['']).filter((_,j) => j !== i); setForm(f => ({ ...f, [key]: a.length ? a : [''] })); }}
            className="text-red-400 hover:text-red-300 text-xs px-2">X</button>
        </div>
      ))}
      <button type="button" onClick={() => setForm(f => ({ ...f, [key]: [...(f[key] || ['']), ''] }))}
        className="text-xs text-cyan-400 hover:text-cyan-300">+ Add</button>
    </div>
  );

  if (loading) return <div className="text-gray-400 p-4">Loading...</div>;

  return (
    <div className="flex gap-4">
      <div className="w-48 flex-shrink-0 space-y-1">
        <button onClick={newCountry} className="w-full text-xs bg-cyan-700 hover:bg-cyan-600 text-white rounded px-2 py-1.5 mb-2">+ New Country</button>
        {countries.map(c => (
          <div key={c.countryCode} onClick={() => select(c)}
            className={'cursor-pointer rounded px-2 py-1.5 text-sm flex justify-between items-center ' + (selected?.countryCode === c.countryCode ? 'bg-cyan-900 text-white' : 'bg-gray-800 hover:bg-gray-700 text-gray-300')}>
            <span>{c.countryCode} - {c.countryName}</span>
            <button onClick={(e) => { e.stopPropagation(); del(c.countryCode); }} className="text-red-400 hover:text-red-300 text-xs">X</button>
          </div>
        ))}
      </div>
      <div className="flex-1 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-white">{selected ? ('Edit: ' + selected.countryName) : 'New Country Profile'}</h3>
          <div className="flex gap-2">
            {selected && <StatusBadge status={selected.status} />}
            <button onClick={save} className="px-3 py-1 text-xs bg-blue-700 hover:bg-blue-600 rounded text-white">Save Draft</button>
            <button onClick={publish} className="px-3 py-1 text-xs bg-green-700 hover:bg-green-600 rounded text-white font-semibold">Publish</button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[['Country Code (2-letter)', 'countryCode'], ['Country Name', 'countryName'], ['Map Link', 'mapLink'], ['WhatsApp Number', 'whatsapp']].map(([lbl, key]) => (
            <div key={key}>
              <label className="block text-xs text-gray-400 mb-1">{lbl}</label>
              <input type="text" value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-cyan-500" />
            </div>
          ))}
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Office Address</label>
          <textarea value={form.officeAddress || ''} onChange={e => setForm(f => ({ ...f, officeAddress: e.target.value }))} rows={2}
            className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-cyan-500" />
        </div>
        {arrField('phones', 'Phone Numbers')}
        {arrField('emails', 'Email Addresses')}
        <div className="grid grid-cols-3 gap-3">
          {[['Weekdays', 'weekdays'], ['Saturday', 'saturday'], ['Sunday', 'sunday']].map(([lbl, key]) => (
            <div key={key}>
              <label className="block text-xs text-gray-400 mb-1">{lbl}</label>
              <input type="text" value={form.businessHours?.[key] || ''} onChange={e => setForm(f => ({ ...f, businessHours: { ...f.businessHours, [key]: e.target.value } }))}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-cyan-500" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// TAB: Pages (Legal / Help / About / FAQ)
// ────────────────────────────────────────────────────────────
const PAGE_TEMPLATES = [
  { slug: 'about',           title: 'About Us',               category: 'about' },
  { slug: 'mission',         title: 'Mission, Vision & Values', category: 'about' },
  { slug: 'terms',           title: 'Terms & Conditions',     category: 'legal' },
  { slug: 'privacy',         title: 'Privacy Policy',         category: 'legal' },
  { slug: 'aml',             title: 'AML Policy',             category: 'legal' },
  { slug: 'kyc-policy',      title: 'KYC Policy',             category: 'legal' },
  { slug: 'risk',            title: 'Risk Disclosure',        category: 'legal' },
  { slug: 'cookie',          title: 'Cookie Policy',          category: 'legal' },
  { slug: 'disclaimer',      title: 'Disclaimer',             category: 'legal' },
  { slug: 'faq',             title: 'FAQ',                    category: 'faq'  },
  { slug: 'help-deposit',    title: 'How to Deposit',         category: 'help' },
  { slug: 'help-withdrawal', title: 'How to Withdraw',        category: 'help' },
  { slug: 'help-usdt',       title: 'USDT / Crypto Guide',    category: 'help' },
  { slug: 'help-investment', title: 'Investment Guide',       category: 'help' },
  { slug: 'help-referral',   title: 'Referral Guide',         category: 'help' },
  { slug: 'help-merchant',   title: 'Merchant Payments Guide', category: 'help' },
  { slug: 'help-internet',   title: 'Internet / DWiFi Guide', category: 'help' },
];

function PagesTab() {
  const [pages, setPages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ slug:'', title:'', content:'', category:'legal' });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    cmsAdmin.getPages().then(r => setPages(r.data || [])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openPage = async (slug) => {
    try {
      const r = await cmsAdmin.getPage(slug);
      setSelected(r.data);
      setForm({ slug: r.data.slug, title: r.data.title, content: r.data.content || '', category: r.data.category });
    } catch { toast.error('Load failed'); }
  };

  const openTemplate = (tpl) => {
    const existing = pages.find(p => p.slug === tpl.slug);
    if (existing) { openPage(tpl.slug); return; }
    setSelected(null);
    setForm({ slug: tpl.slug, title: tpl.title, content: '', category: tpl.category });
  };

  const save = async () => {
    try { await cmsAdmin.savePage(form); toast.success('Saved as draft'); load(); }
    catch (e) { toast.error(e.message || 'Save failed'); }
  };

  const publish = async () => {
    try {
      await cmsAdmin.savePage(form);
      await cmsAdmin.publishPage(form.slug);
      toast.success('Published'); load();
    } catch (e) { toast.error(e.message || 'Publish failed'); }
  };

  const rollback = async () => {
    if (!window.confirm('Roll back to previous version?')) return;
    try {
      const r = await cmsAdmin.rollbackPage(form.slug);
      if (r.data) setForm(f => ({ ...f, content: r.data.content || '' }));
      toast.success('Rolled back');
    } catch (e) { toast.error(e.message || 'Rollback failed'); }
  };

  const categories = ['about', 'legal', 'faq', 'help'];
  const dbSlugs = new Set(pages.map(p => p.slug));

  if (loading) return <div className="text-gray-400 p-4">Loading...</div>;

  return (
    <div className="flex gap-4">
      <div className="w-56 flex-shrink-0 space-y-3 overflow-y-auto" style={{maxHeight:'75vh'}}>
        {categories.map(cat => (
          <div key={cat}>
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">{cat}</div>
            {PAGE_TEMPLATES.filter(p => p.category === cat).map(tpl => {
              const exists = dbSlugs.has(tpl.slug);
              const isSelected = form.slug === tpl.slug;
              return (
                <div key={tpl.slug} onClick={() => openTemplate(tpl)}
                  className={'cursor-pointer rounded px-2 py-1.5 text-xs flex justify-between items-center ' + (isSelected ? 'bg-cyan-900 text-white' : 'bg-gray-800 hover:bg-gray-700 text-gray-300')}>
                  <span>{tpl.title}</span>
                  {exists && <span className="text-green-400 text-xs">&#9679;</span>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className="flex-1 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-base font-semibold text-white">{form.title || 'Select a page'}</h3>
            {selected && <StatusBadge status={selected.status} />}
          </div>
          <div className="flex gap-2">
            {selected && <button onClick={rollback} className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded text-white">Rollback</button>}
            <button onClick={save} disabled={!form.slug} className="px-3 py-1 text-xs bg-blue-700 hover:bg-blue-600 rounded text-white disabled:opacity-50">Save Draft</button>
            <button onClick={publish} disabled={!form.slug} className="px-3 py-1 text-xs bg-green-700 hover:bg-green-600 rounded text-white font-semibold disabled:opacity-50">Publish</button>
          </div>
        </div>
        {form.slug ? (
          <RichEditor value={form.content} onChange={v => setForm(f => ({ ...f, content: v }))} />
        ) : (
          <div className="text-gray-500 text-sm py-10 text-center">Select a page from the list to edit its content.</div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// TAB: Banners
// ────────────────────────────────────────────────────────────
const BANNER_TYPES = ['hero', 'announcement', 'news', 'promotion', 'investment-ad', 'internet-package', 'maintenance', 'scrolling-alert'];

function BannersTab() {
  const [banners, setBanners] = useState([]);
  const [form, setForm] = useState({ type:'announcement', title:'', body:'', imageUrl:'', ctaLabel:'', ctaUrl:'', isActive:true, priority:0 });
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');

  const load = useCallback(() => {
    cmsAdmin.getBanners(typeFilter || undefined).then(r => setBanners(r.data || [])).finally(() => setLoading(false));
  }, [typeFilter]);

  useEffect(() => { load(); }, [load]);

  const edit = (b) => { setEditing(b._id); setForm({ type: b.type, title: b.title, body: b.body||'', imageUrl: b.imageUrl||'', ctaLabel: b.ctaLabel||'', ctaUrl: b.ctaUrl||'', isActive: b.isActive, priority: b.priority||0 }); };

  const reset = () => { setEditing(null); setForm({ type:'announcement', title:'', body:'', imageUrl:'', ctaLabel:'', ctaUrl:'', isActive:true, priority:0 }); };

  const save = async () => {
    try {
      if (editing) { await cmsAdmin.updateBanner(editing, form); toast.success('Updated'); }
      else { await cmsAdmin.createBanner(form); toast.success('Created'); }
      reset(); load();
    } catch (e) { toast.error(e.message || 'Save failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    try { await cmsAdmin.deleteBanner(id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  const inp = (label, key, type = 'text') => (
    <div key={key}>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <input type={type} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: type === 'number' ? Number(e.target.value) : e.target.value }))}
        className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-cyan-500" />
    </div>
  );

  if (loading) return <div className="text-gray-400 p-4">Loading...</div>;

  return (
    <div className="space-y-5">
      <div className="bg-gray-800 rounded-lg p-4 space-y-3">
        <h3 className="text-sm font-semibold text-white">{editing ? 'Edit Banner' : 'New Banner'}</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Type</label>
            <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
              className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-1.5 text-sm text-white focus:outline-none">
              {BANNER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {inp('Title', 'title')}
          {inp('CTA Label', 'ctaLabel')}
          {inp('CTA URL', 'ctaUrl', 'url')}
          {inp('Image URL', 'imageUrl', 'url')}
          {inp('Priority (higher = first)', 'priority', 'number')}
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Body</label>
          <textarea value={form.body || ''} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} rows={3}
            className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-cyan-500" />
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
            <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} className="rounded" />
            Active
          </label>
          <div className="flex gap-2 ml-auto">
            {editing && <button onClick={reset} className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded text-white">Cancel</button>}
            <button onClick={save} className="px-4 py-1 text-xs bg-green-700 hover:bg-green-600 rounded text-white font-semibold">{editing ? 'Update' : 'Create'}</button>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-400">Filter:</span>
        {['', ...BANNER_TYPES].map(t => (
          <button key={t} onClick={() => setTypeFilter(t)}
            className={'px-2 py-1 text-xs rounded ' + (typeFilter === t ? 'bg-cyan-700 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700')}>
            {t || 'All'}
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {banners.length === 0 && <p className="text-gray-500 text-sm">No banners yet.</p>}
        {banners.map(b => (
          <div key={b._id} className="bg-gray-800 rounded-lg px-4 py-3 flex items-center justify-between gap-4">
            <div>
              <span className="text-xs bg-gray-700 text-gray-300 rounded px-1.5 py-0.5 mr-2">{b.type}</span>
              <span className="text-sm text-white font-medium">{b.title}</span>
              {!b.isActive && <span className="ml-2 text-xs text-yellow-400">Inactive</span>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => edit(b)} className="text-xs text-cyan-400 hover:text-cyan-300">Edit</button>
              <button onClick={() => del(b._id)} className="text-xs text-red-400 hover:text-red-300">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// TAB: Social Media
// ────────────────────────────────────────────────────────────
function SocialTab() {
  const [form, setForm] = useState({ facebook:'', instagram:'', linkedin:'', twitter:'', telegram:'', youtube:'', tiktok:'', whatsapp:'', status:'published' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cmsAdmin.getSocial().then(r => { if (r.data && r.data._id) setForm(f => ({ ...f, ...r.data })); }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    try { await cmsAdmin.updateSocial(form); toast.success('Saved as draft'); }
    catch (e) { toast.error(e.message || 'Save failed'); }
  };

  const publish = async () => {
    try {
      await cmsAdmin.updateSocial(form);
      await cmsAdmin.publishSocial();
      setForm(f => ({ ...f, status: 'published' }));
      toast.success('Published');
    } catch (e) { toast.error(e.message || 'Publish failed'); }
  };

  const platforms = [
    ['Facebook', 'facebook'], ['Instagram', 'instagram'], ['LinkedIn', 'linkedin'],
    ['X (Twitter)', 'twitter'], ['Telegram', 'telegram'], ['YouTube', 'youtube'],
    ['TikTok', 'tiktok'], ['WhatsApp', 'whatsapp'],
  ];

  if (loading) return <div className="text-gray-400 p-4">Loading...</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Social Media Links</h2>
        <div className="flex gap-2">
          <StatusBadge status={form.status} />
          <button onClick={save} className="px-3 py-1.5 text-xs bg-blue-700 hover:bg-blue-600 rounded text-white">Save Draft</button>
          <button onClick={publish} className="px-3 py-1.5 text-xs bg-green-700 hover:bg-green-600 rounded text-white font-semibold">Publish</button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {platforms.map(([label, key]) => (
          <div key={key}>
            <label className="block text-xs text-gray-400 mb-1">{label}</label>
            <input type="url" value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
              placeholder="https://..."
              className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// TAB: Version & System Status
// ────────────────────────────────────────────────────────────
function VersionTab() {
  const [form, setForm] = useState({ appVersion:'1.0.0', releaseDate:'', apiStatus:'operational', systemStatus:'operational', releaseNotes:'', status:'published' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cmsAdmin.getVersion().then(r => {
      if (r.data && r.data._id) {
        const d = r.data;
        setForm(f => ({ ...f, ...d, releaseDate: d.releaseDate ? d.releaseDate.split('T')[0] : '' }));
      }
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    try { await cmsAdmin.updateVersion(form); toast.success('Saved as draft'); }
    catch (e) { toast.error(e.message || 'Save failed'); }
  };

  const publish = async () => {
    try {
      await cmsAdmin.updateVersion(form);
      await cmsAdmin.publishVersion();
      setForm(f => ({ ...f, status: 'published' }));
      toast.success('Published');
    } catch (e) { toast.error(e.message || 'Publish failed'); }
  };

  if (loading) return <div className="text-gray-400 p-4">Loading...</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Version & System Status</h2>
        <div className="flex gap-2">
          <StatusBadge status={form.status} />
          <button onClick={save} className="px-3 py-1.5 text-xs bg-blue-700 hover:bg-blue-600 rounded text-white">Save Draft</button>
          <button onClick={publish} className="px-3 py-1.5 text-xs bg-green-700 hover:bg-green-600 rounded text-white font-semibold">Publish</button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1">App Version</label>
          <input type="text" value={form.appVersion} onChange={e => setForm(f => ({ ...f, appVersion: e.target.value }))}
            className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500" />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Release Date</label>
          <input type="date" value={form.releaseDate} onChange={e => setForm(f => ({ ...f, releaseDate: e.target.value }))}
            className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500" />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">API Status</label>
          <select value={form.apiStatus} onChange={e => setForm(f => ({ ...f, apiStatus: e.target.value }))}
            className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm text-white focus:outline-none">
            <option value="operational">Operational</option>
            <option value="degraded">Degraded</option>
            <option value="outage">Outage</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">System Status</label>
          <select value={form.systemStatus} onChange={e => setForm(f => ({ ...f, systemStatus: e.target.value }))}
            className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm text-white focus:outline-none">
            <option value="operational">Operational</option>
            <option value="maintenance">Maintenance</option>
            <option value="incident">Incident</option>
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs text-gray-400 mb-1">Release Notes</label>
        <textarea value={form.releaseNotes} onChange={e => setForm(f => ({ ...f, releaseNotes: e.target.value }))} rows={5}
          className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-cyan-500" />
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// TAB: Audit Log
// ────────────────────────────────────────────────────────────
function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    const params = { limit: 100 };
    if (filter) params.model = filter;
    cmsAdmin.getAudit(params).then(r => { setLogs(r.data || []); setTotal(r.total || 0); }).finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const models = ['CmsCompanyProfile', 'CmsCountryProfile', 'CmsPage', 'CmsBanner', 'CmsSocialMedia', 'CmsVersion'];

  if (loading) return <div className="text-gray-400 p-4">Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">CMS Audit Log</h2>
        <span className="text-xs text-gray-400">{total} entries</span>
      </div>
      <div className="flex gap-2 flex-wrap">
        {['', ...models].map(m => (
          <button key={m} onClick={() => setFilter(m)}
            className={'px-2 py-1 text-xs rounded ' + (filter === m ? 'bg-cyan-700 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700')}>
            {m || 'All'}
          </button>
        ))}
      </div>
      <div className="space-y-1" style={{maxHeight:'600px', overflowY:'auto'}}>
        {logs.length === 0 && <p className="text-gray-500 text-sm">No audit entries yet.</p>}
        {logs.map(log => (
          <div key={log._id} className="bg-gray-800 rounded px-3 py-2 flex flex-wrap gap-3 items-start text-xs">
            <span className="text-gray-400 shrink-0">{new Date(log.createdAt).toLocaleString()}</span>
            <span className={'font-semibold shrink-0 ' + (log.action === 'published' ? 'text-green-400' : log.action === 'deleted' ? 'text-red-400' : log.action === 'rolled_back' ? 'text-yellow-400' : 'text-cyan-400')}>{log.action.toUpperCase()}</span>
            <span className="text-gray-400 shrink-0">{(log.model || '').replace('Cms','')}</span>
            <span className="text-gray-300 flex-1">{log.summary || log.field || ''}</span>
            <span className="text-gray-500 shrink-0">{log.adminEmail}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// MAIN: CmsAdmin page
// ════════════════════════════════════════════════════════════
const TABS = [
  { id: 'company',   label: 'Company' },
  { id: 'countries', label: 'Countries' },
  { id: 'pages',     label: 'Pages & Docs' },
  { id: 'banners',   label: 'Banners' },
  { id: 'social',    label: 'Social' },
  { id: 'version',   label: 'Version' },
  { id: 'audit',     label: 'Audit Log' },
];

export default function CmsAdmin() {
  const [tab, setTab] = useState('company');

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4">
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">Content Management System</h1>
            <p className="text-xs text-gray-400 mt-0.5">Single source of truth for all public content</p>
          </div>
          <a href="/admin" className="text-xs text-cyan-400 hover:text-cyan-300">Back to Admin</a>
        </div>

        <div className="flex gap-1 flex-wrap bg-gray-900 rounded-xl p-1">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ' + (tab === t.id ? 'bg-cyan-700 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800')}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="bg-gray-900 rounded-xl p-5">
          {tab === 'company'   && <CompanyTab />}
          {tab === 'countries' && <CountriesTab />}
          {tab === 'pages'     && <PagesTab />}
          {tab === 'banners'   && <BannersTab />}
          {tab === 'social'    && <SocialTab />}
          {tab === 'version'   && <VersionTab />}
          {tab === 'audit'     && <AuditTab />}
        </div>
      </div>
    </div>
  );
}
